import React from 'react';

import { css } from '@emotion/react';
import { CssVars, NeutralColorToken, Type } from '@robinhood/design';
import { MonthFullDate } from '@x1/util-format';
import moment from 'moment';
import QRCode from 'react-qr-code';

import Cell from 'src/components/creditApplication/Cell';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import { IMAGES } from 'src/constants/assets';
import { Campaign, getAttribution } from 'src/hooks/useAttribution';
import { useGlobalStore } from 'src/state/global';

const IMAGE_HEIGHT = 140;

const DownloadAppPageDesktop = () => {
  const { utmParams } = getAttribution();
  const isAgenticCard = utmParams?.campaign === Campaign.AgenticCard;
  const shippingMethod = useGlobalStore((s) => s.shippingMethod);
  const subtitle = React.useMemo(() => {
    if (shippingMethod == null) {
      return null;
    }
    const eta = shippingMethod?.eta != null ? MonthFullDate(moment(shippingMethod?.eta)) : null;
    const etaEnd = shippingMethod?.etaEnd != null ? MonthFullDate(moment(shippingMethod?.etaEnd)) : null;
    const etaString = eta && etaEnd ? `${eta} - ${etaEnd}` : eta || etaEnd || '';
    return (
      <>
        {etaString && (
          <>
            Your Gold Card arrives {etaString}
            <br />
          </>
        )}
        {shippingMethod?.carrier && <>Shipping via {shippingMethod?.carrier}</>}
      </>
    );
  }, [shippingMethod]);

  return (
    <CreditApplicationPageLayout title="There's Gold on the horizon" subtitle={subtitle} maxContentWidth={880}>
      <div css={contentContainer}>
        <Cell css={cell}>
          <QRCode
            bgColor={NeutralColorToken.Bg1}
            fgColor='#cfb97d' // SemanticColorToken.Positive doesn't work here
            value='https://robinhood.com/creditcard/download-app'
            size={IMAGE_HEIGHT}
          />
          <div css={textContainer}>
            <Type.Display size='M' overrideRootStyles={fontWeight400}>
              Download the Banking app to get your Gold Card
            </Type.Display>
            <Type.Text size='S'>
              Your Gold Card is ready to use immediately.
              <br />
              Get your card number in the app.
            </Type.Text>
          </div>
        </Cell>
        {isAgenticCard && (
          <Cell css={cell}>
            <img src={IMAGES.agenticCardFront} alt='Agentic Card Front' css={agenticCardFrontImage} />
            <div css={textContainer}>
              <Type.Display size='M' overrideRootStyles={fontWeight400}>
                Continue your agentic card onboarding
              </Type.Display>
              <Type.Text size='M'>Authenticate your agent to the Robinhood Banking MCP.</Type.Text>
            </div>
          </Cell>
        )}
      </div>
    </CreditApplicationPageLayout>
  );
};

const fontWeight400 = css`
  font-weight: 400;
`;

const contentContainer = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 40px;
`;

const cell = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;
  padding: 72px 40px 64px 40px;
  max-width: 420px;
  background-color: ${CssVars.neutral.bg1};
`;

const textContainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: center;
  max-width: 340px;
  gap: 12px;
`;

const agenticCardFrontImage = css`
  height: ${IMAGE_HEIGHT}px;
  object-fit: cover;
`;

export default DownloadAppPageDesktop;
